/**
 * Surfer-style colour levels for inversion sections: explicit, editable class breaks
 * (min, max, interval/count) instead of a silently auto-stretched ramp. Pure helpers.
 */

export const MAX_BREAKS = 40;

const hexStops = (arr) => arr.map((c, i) => [i / (arr.length - 1), c]);
const EARTHIMAGER = [[37, 99, 235], [6, 182, 212], [52, 211, 153], [251, 191, 36], [251, 146, 60], [248, 113, 113]]
  .map((c, i) => [i / 5, `rgb(${c[0]},${c[1]},${c[2]})`]);

const STOPS = {
  earthimager: EARTHIMAGER,
  viridis: hexStops(['#440154', '#472c7a', '#3b518b', '#2c718e', '#21908d', '#27ad81', '#5cc863', '#aadc32', '#fde725']),
  turbo: hexStops(['#30123b', '#4662d7', '#36aaf9', '#1ae4b6', '#72fe5e', '#c8ef34', '#faba39', '#f66b19', '#ca2a04', '#7a0403']),
  gray: [[0, '#000000'], [1, '#ffffff']],
  terrain: [[0, '#333399'], [0.15, '#0099ff'], [0.25, '#00cc66'], [0.5, '#ffff99'], [0.75, '#805c54'], [1, '#ffffff']],
  bwr: [[0, '#2166ac'], [0.5, '#f7f7f7'], [1, '#b2182b']],
};
export const SECTION_COLORMAPS = Object.keys(STOPS);

const rgbOf = (s) => {
  if (s[0] === '#') return [1, 3, 5].map(i => parseInt(s.slice(i, i + 2), 16));
  return s.match(/\d+/g).map(Number);
};

/** Colour (css rgb string) at t in [0,1] on the named ramp. */
export function rampColor(name, t, reverse = false) {
  const stops = STOPS[name] || STOPS.earthimager;
  let u = Math.max(0, Math.min(1, Number.isFinite(t) ? t : 0));
  if (reverse) u = 1 - u;
  let i = 0;
  while (i < stops.length - 2 && u > stops[i + 1][0]) i++;
  const [p0, c0] = stops[i], [p1, c1] = stops[i + 1];
  const f = p1 > p0 ? (u - p0) / (p1 - p0) : 0;
  const a = rgbOf(c0), b = rgbOf(c1);
  const rgb = a.map((v, k) => Math.round(v + (b[k] - v) * f));
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

/**
 * Sorted class breaks. Linear: min, min+interval, ... max. Log: equal steps in log10
 * (interval is in decades). `interval` wins over `count` (default 10 classes).
 * Never more than MAX_BREAKS breaks.
 */
export function makeLevels({ min, max, log = false, interval = null, count = 10 } = {}) {
  let lo = Number(min), hi = Number(max);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [];
  if (log) {
    if (lo <= 0) lo = hi > 0 ? hi / 1e3 : 1e-6;
    lo = Math.log10(lo); hi = Math.log10(hi);
  }
  if (!(hi > lo)) hi = lo + 1;
  const span = hi - lo;
  const iv = Number(interval);
  const useInterval = interval !== null && interval !== '' && Number.isFinite(iv) && iv > 0;
  let n = useInterval ? Math.ceil(span / iv - 1e-9) : (Math.round(Number(count)) || 10);
  const step = useInterval && n <= MAX_BREAKS - 1 ? iv : span / Math.max(1, Math.min(MAX_BREAKS - 1, n));
  n = Math.max(1, Math.min(MAX_BREAKS - 1, n));
  const out = [];
  for (let k = 0; k < n; k++) out.push(lo + k * step);
  out.push(hi);
  return out.map(v => (log ? Math.pow(10, v) : Math.round(v * 1e9) / 1e9));
}

/** Class index (0..n-1) of value among sorted breaks; values outside clamp to the end classes. */
export function classify(value, breaks) {
  const n = breaks.length - 1;
  if (n < 1) return 0;
  let k = 0;
  while (k < n && value >= breaks[k + 1]) k++;
  return Math.max(0, Math.min(n - 1, k));
}

/** Format one value with 3 significant digits. */
export function fmtLevel(v) {
  if (!Number.isFinite(v)) return '';
  if (v !== 0 && (Math.abs(v) >= 1e5 || Math.abs(v) < 1e-3)) return Number(v.toPrecision(3)).toExponential().replace('e+', 'e');
  return String(Number(v.toPrecision(3)));
}

/** Legend labels for the breaks (log: 1, 10, 100 style when on a decade). */
export function legendTicks(breaks, log = false) {
  return breaks.map((b) => {
    if (log && b > 0) {
      const l = Math.log10(b);
      if (Math.abs(l - Math.round(l)) < 1e-9) return fmtLevel(Math.pow(10, Math.round(l)));
    }
    return fmtLevel(b);
  });
}
